import { IDropdownOption } from '@fluentui/react';
import { IColumn } from '@fluentui/react/lib/DetailsList';

import { SelectAsyncConfig } from '../../components/AdminSettings/components/BasicEntityModelComponents/types';
import { DAIEngineProfile } from '../gen/ai/h2o/engine/v1/dai_engine_profile_pb';
import { H2OEngineProfile } from '../gen/ai/h2o/engine/v1/h2o_engine_profile_pb';
import { NotebookEngineProfile } from '../gen/ai/h2o/engine/v1/notebook_engine_profile_pb';
import { RPC } from '../gen/runtime';
import { ConstraintType } from '../types';
import { EntityActionType, EntityFieldType, EntityType } from './constants';

export enum DependencyOperator {
  Equals = 'equals',
  NotEquals = 'notEquals',
  Truthy = 'truthy',
  Falsy = 'falsy',
}

export type EngineType = 'DAI' | 'H2O' | 'Notebook' | 'Sandbox';

export type EntityFieldEngineProfile = DAIEngineProfile | H2OEngineProfile | NotebookEngineProfile;

export type HasName = {
  name?: string;
};

export type EntityActionTypeMSWRestHTTPMethod = {
  [key in EntityActionType]: 'post' | 'get' | 'patch' | 'put' | 'delete';
};

export type EntityAction<EntityModel, EntityRequest, EntityResponse> = {
  name: string;
  type: EntityActionType;
  entityType: EntityType;
  rpc: RPC<EntityRequest, EntityResponse>;
  mockPath?: string; // if we need a special path for mocks - e.g. if the path definition includes delimiters or interpolation
  mockResponse?: EntityResponse | ((req: EntityRequest) => EntityResponse);
  responseKey?: keyof EntityResponse;
  requestNameKey?: keyof EntityRequest;
  requestPayloadKey?: keyof EntityRequest;
  payloadKey?: keyof EntityModel;
  payloadIdKey?: keyof EntityModel | keyof HasName;
};

type ToggleableFieldConfig =
  | {
      togglable?: false;
      fieldUnsetText?: never;
    }
  | {
      togglable: true;
      fieldUnsetText?: string;
    };

export type EntityFieldBase<EntityModel> = {
  name: keyof EntityModel;
  type: EntityFieldType;
  label: string;
  referenceName?: keyof EntityModel;
  description?: string;
  required?: boolean;
  disabled?: boolean;
  options?: IDropdownOption[];
  selectedOption?: string;
  relayValueTo?: [keyof EntityModel];
  readOnlyOnEdit?: boolean;
  constraintType?: ConstraintType;
  selectAsyncConfig?: SelectAsyncConfig;
  collapsedGroup?: string;
  inputDefaultValue?: string;
  permitActions?: string[];
  dependsOn?: {
    field: keyof EntityModel;
    value?: any;
    operator?: DependencyOperator;
  };
};

export type NumberEntityField<EntityModel> = EntityFieldBase<EntityModel> & {
  type: EntityFieldType.Number | EntityFieldType.UnlimitedNumber | EntityFieldType.Bytes;
  minimum?: string | number;
  maximum?: string | number;
  minimumField?: keyof EntityModel;
  maximumField?: keyof EntityModel;
  convertToGibibytes?: boolean;
} & ToggleableFieldConfig;

export type DurationEntityField<EntityModel> = EntityFieldBase<EntityModel> & {
  type: EntityFieldType.Duration;
  minimum?: string | number;
  maximum?: string | number;
} & ToggleableFieldConfig;

export type SemverEntityField<EntityModel> = EntityFieldBase<EntityModel> & {
  type: EntityFieldType.Semver;
  minimum?: string | number;
};

export type SelectEnumEntityField<EntityModel> = EntityFieldBase<EntityModel> & {
  type: EntityFieldType.SelectEnum;
  options: IDropdownOption[];
  selectedOption?: string;
};

type BaseFieldTypes = Exclude<
  EntityFieldType,
  | EntityFieldType.Number
  | EntityFieldType.UnlimitedNumber
  | EntityFieldType.Bytes
  | EntityFieldType.Duration
  | EntityFieldType.Semver
  | EntityFieldType.SelectEnum
>;

export type BasicEntityField<EntityModel> = EntityFieldBase<EntityModel> & {
  type: BaseFieldTypes;
};

export type EntityField<EntityModel> =
  | BasicEntityField<EntityModel>
  | DurationEntityField<EntityModel>
  | NumberEntityField<EntityModel>
  | SemverEntityField<EntityModel>
  | SelectEnumEntityField<EntityModel>;

export type Entity<EntityModel> = {
  type: EntityType;
  name: string; // name id
  displayName: string; // to be used with tab labels
  description?: string;
  createButtonLabel?: string;
  actions: { [key in EntityActionType]?: EntityAction<EntityModel, any, any> };
  fields: EntityField<EntityModel>[]; // form fields
  columns: IColumn[]; // table columns
  order?: number;
  emptyModel: EntityModel; // empty model for create
  canBeReordered: boolean;
  filterFields?: (keyof EntityModel)[];
  sortFields?: (keyof EntityModel)[];
  requestNameKeyPrefix?: string;
  displayNameKey: keyof EntityModel;
};

export type MessageText = {
  group?: string;
  subject?: string;
  action?: string;
  object?: string;
};
